import { ALL_LNGS_NAMESPACES } from '@ingenico/util-i18n-config';
import type { TFunction } from 'i18next';
import { toString } from 'lodash-es';

const BYTE_SIZE_UNITS_BASE10 = ['Bytes', 'KB', 'MB', 'GB', 'TB', 'PB', 'EB', 'ZB', 'YB'];

export function calculateMagnitute10(bytes: number): number {
  return Math.max(Math.floor(Math.log10(bytes) / 3), 0);
}
export function calculateMagnitute2(bytes: number): number {
  return Math.max(Math.floor(Math.log2(bytes) / 10), 0);
}

export function calculateFileSuffixBase10(sizeByte: number): string {
  if (sizeByte <= 1) {
    return BYTE_SIZE_UNITS_BASE10[0];
  }

  const unitIndex = calculateMagnitute10(sizeByte);

  if (unitIndex >= BYTE_SIZE_UNITS_BASE10.length) {
    return toString(BYTE_SIZE_UNITS_BASE10.at(-1));
  }
  return BYTE_SIZE_UNITS_BASE10[unitIndex];
}
export function calculateFileSuffixBase2(sizeByte: number): string {
  if (sizeByte <= 1) {
    return BYTE_SIZE_UNITS_BASE10[0];
  }

  const unitIndex = calculateMagnitute2(sizeByte);

  if (unitIndex >= BYTE_SIZE_UNITS_BASE10.length) {
    return toString(BYTE_SIZE_UNITS_BASE10.at(-1));
  }
  return BYTE_SIZE_UNITS_BASE10[unitIndex];
}

export function calculateFileSizeBase10(bytes: number, significantDigits = 2): string {
  if (bytes <= 0) {
    return Number.parseFloat(bytes.toFixed(significantDigits)).toString();
  }
  const rounder = 10 ** significantDigits;
  const unitIndex = calculateMagnitute10(bytes) * 3;
  const calculatedDivision = bytes / 10 ** unitIndex;
  const roundedNumber = (Math.floor(calculatedDivision * rounder) / rounder).toFixed(significantDigits);
  const output = Number.parseFloat(roundedNumber).toString();
  return output;
}

export function calculateFileSizeBase2(bytes: number, significantDigits = 2): string {
  if (bytes <= 0) {
    return Number.parseFloat(bytes.toFixed(significantDigits)).toString();
  }
  const rounder = 10 ** 3;
  const unitIndex = calculateMagnitute2(bytes) * 10;
  const calculatedDivision = bytes / 2 ** unitIndex;
  const roundedNumber = (Math.floor(calculatedDivision * rounder) / rounder).toFixed(significantDigits);
  const output = Number.parseFloat(roundedNumber).toString();
  return output;
}

export function getHumanizedFileSize10(bytes: number, t?: TFunction, significant = 2): string {
  const fileSuffixTranslation = t
    ? t(`byteSizeUnit.${calculateFileSuffixBase10(bytes).toLowerCase()}`, { ns: ALL_LNGS_NAMESPACES.COMMON })
    : calculateFileSuffixBase10(bytes);
  return `${calculateFileSizeBase10(bytes, significant)} ${fileSuffixTranslation}`;
}
export function getHumanizedFileSize2(bytes: number, t?: TFunction, significant = 2): string {
  const fileSuffixTranslation = t
    ? t(`byteSizeUnit.${calculateFileSuffixBase2(bytes).toLowerCase()}`, { ns: ALL_LNGS_NAMESPACES.COMMON })
    : calculateFileSuffixBase2(bytes);
  return `${calculateFileSizeBase2(bytes, significant)} ${fileSuffixTranslation}`;
}

export function getHumanizedFileSize(bytes: number, t?: TFunction, base = 10, significant = 2): string {
  if (base === 2) {
    return getHumanizedFileSize2(bytes, t, significant);
  }
  return getHumanizedFileSize10(bytes, t, significant);
}

export function getOptimalFileSizeUnit(sizeInMB: number): { size: number; unit: 'Bytes' | 'GB' | 'KB' | 'MB' } {
  const sizeInBytes = sizeInMB * 1000 * 1000; // Use base 10 to match getHumanizedFileSize

  // Use getHumanizedFileSize and parse the result "2 GB" → { size: 2, unit: 'GB' }
  const humanizedSize = getHumanizedFileSize(sizeInBytes, undefined, 10, 2); // 2 decimals to preserve precision
  const [sizeStr, unit] = humanizedSize.trim().split(' ');

  return unit === 'Bytes' || unit === 'KB' || unit === 'MB' || unit === 'GB'
    ? {
        size: Number.parseFloat(sizeStr),
        unit,
      }
    : {
        // Fallback for unsupported units (TB, PB, etc.) → keep in MB
        size: sizeInMB,
        unit: 'MB',
      };
}
